import { supabase } from '../../supabase';
import type { Snap, SnapCategory } from '@/types/database.types';
import { getLikeStatus } from '../likes';

export async function createSnap(
  title: string,
  description: string,
  isAnonymous: boolean,
  durationDays: number,
  category: SnapCategory,
  options: { title: string; description: string; imageUrl: string }[]
): Promise<Snap> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('User not authenticated');

  const { data: snap, error: snapError } = await supabase
    .from('snaps')
    .insert({
      title,
      description,
      creator_id: user.id,
      is_anonymous: isAnonymous,
      duration_days: durationDays,
      category,
      ends_at: new Date(Date.now() + durationDays * 24 * 60 * 60 * 1000).toISOString()
    })
    .select(`
      *,
      creator:user_profiles!inner(
        id,
        username,
        avatar_url,
        created_at
      )
    `)
    .single();

  if (snapError) throw snapError;

  for (const option of options) {
    const { error: optionError } = await supabase
      .from('snap_options')
      .insert({
        snap_id: snap.id,
        title: option.title,
        description: option.description || '',
        image_url: option.imageUrl || ''
      });

    if (optionError) {
      await supabase.from('snaps').delete().eq('id', snap.id);
      throw optionError;
    }
  }

  // Get initial like status
  const likeStatus = await getLikeStatus(snap.id);

  return {
    ...snap,
    options: [],
    isOwner: true,
    commentCount: 0,
    userLiked: likeStatus.liked,
    likes_count: likeStatus.count
  };
}

export async function updateSnap(
  id: string,
  data: {
    title?: string;
    description?: string;
    is_anonymous?: boolean;
    duration_days?: number;
    ends_at?: string;
  }
): Promise<void> {
  const { error } = await supabase
    .from('snaps')
    .update(data)
    .eq('id', id);

  if (error) {
    console.error('Error updating snap:', error);
    throw new Error('Failed to update SNAP');
  }
}

export async function deleteSnap(id: string): Promise<void> {
  const { error } = await supabase
    .from('snaps')
    .delete()
    .eq('id', id);

  if (error) {
    console.error('Error deleting snap:', error);
    throw new Error('Failed to delete SNAP');
  }
}

export async function closeSnap(id: string): Promise<void> {
  const { error } = await supabase
    .from('snaps')
    .update({ ends_at: new Date().toISOString() })
    .eq('id', id);

  if (error) {
    console.error('Error closing snap:', error);
    throw new Error('Failed to close SNAP');
  }
}